/**
 * revokeInvite — RPC contract for the revokeInvite callable.
 *
 * `invite.entity.ts` marks `revoke` `.requiresHandler('revokeInvite')` — a
 * Firestore `.when(...)` rule can gate on `hasPermission(...)` + `me.mfa` +
 * the doc's own `status` off the workspace-nested path, but target-role
 * containment (the invite's OWN role must be ⊆ the caller's own
 * permissions) is a cross-document comparison no declarative rule can
 * express. `revokeInvite.api.ts` (onCall) resolves the caller's authority
 *
 * Fields justified:
 *   token — the pending invite to revoke; doc id of
 *           `workspaces/{orgId}/invites/{token}`. The handler rejects a
 *           token that doesn't resolve to a `pending` invite (already
 *           redeemed/revoked/expired).
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const RevokeInviteInput = z
	.object({
		orgId: Id,
		token: z
			.string()
			.min(1)
			.describe(
				"the pending invite to revoke — doc id of workspaces/{orgId}/invites/{token}",
			),
	})
	.strict();
export type RevokeInviteInput = z.infer<typeof RevokeInviteInput>;

export const RevokeInviteOutput = z.object({ ok: z.literal(true) }).strict();
export type RevokeInviteOutput = z.infer<typeof RevokeInviteOutput>;
